import * as React from "react";
import * as _ from "lodash";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";
import * as moment from "moment-timezone";

import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { breakpoint } from "src/library/utils/styled-media-queries";
import { ButtonLink } from "src/library/components/Link";
import HtmlBody from "src/library/components/Notes/HtmlBody";
import { Text } from "src/library/components/Text";
import Grid from "src/library/components/Hedron";
import { Barcode } from "src/library/components/Barcode";
import { S3Image } from "src/core/shared/components/S3";
import ProductInfoSidebar from "src/core/products/components/ProductInfoSidebar";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { CreateOrderContext } from "src/core/shared/components/HeaderContext/CreateOrderContext";
import ProductTagList from "src/modules/product-tags/components/ProductTagList";
import { canListOrderProduct } from "src/core/orders/utils/orders";
import type { IStore } from "src/core/shared/store";
import useList from "src/core/lists/hooks/useList";
import { useOrder } from "src/core/orders/hooks";
import { useProduct } from "src/core/products/hooks";
import { PageProps } from "src/pages/Router/routes";
import BrandedImage from "src/core/shared/components/BrandedImage";
import { GetProductResponse } from "src/core/products/api/products.api";
import {
  ICurrentOrder,
  OrderOrigin
} from "src/core/orders/api/orders.api";
import useOrderingNotAvailableForDay from "src/core/orders/hooks/validation/useOrderingAvailableForDay";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import ProductStatuses from "src/core/products/components/ProductStatuses";
import ProductNutritionValuesTable from "src/core/products/components/ProductNutritionValuesTable";
import { productToGTMItem } from "src/core/products/utils/product-to-gtm-item";
import { useGTMEvent } from "src/core/shared/hooks/useGTMEvent";
import PromotionalTagLabel, { AvailablePosition } from "src/modules/promotions/components/PromotionalTagLabel";
import { useCurrentUser } from "src/core/users/hooks";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { PartNumber } from "src/core/products/PartNumber";
import { ProductComponentLayoutDetails } from "src/core/products/components/ProductDetails/ProductComponentLayoutDetails";
import { ProductOrderingControls } from "src/core/products/components/ProductDetails/ProductOrderingControls";
import { useProductIsAllowedSubs } from "src/core/products/hooks/useProductIsAllowedSubs";
import { useDrop } from "src/core/drops/hooks";
import { IDrop } from "src/core/drops/api/drops.api";
import OrderVariantLabel from "src/core/orders/components/orderVariantLabel";
import { useProductOrderVariant } from "src/core/orders/hooks/useProductOrderVariant";

/**
 * Main "Products" View
 *
 * @returns
 */
const ViewProductDetailsPage: React.ReactFCWithChildren<PageProps<unknown, { productId: string }>> = props => {
  const { match, terminology } = props;
  const productId = match ? parseInt(match.params.productId, 0) : 0;
  const [ { currentRole } ] = useCurrentUser();

  return (
    <Layout {...props}>
      <>
        <ProductPageInner
          productId={productId}
          terminology={terminology}
          showSideBar={currentRole?.type === "admin"}
        />
      </>
    </Layout>
  );
};

interface ProductPageInnerProps {
  productId: number;
  terminology: ITerminology;
  showSideBar: boolean;
}
export const ProductPageInner: React.FC<ProductPageInnerProps> = ({
  productId, terminology, showSideBar
}) => {
  const gtmEvent = useGTMEvent();
  const getProductOrderVariant = useProductOrderVariant();

  const { role, installedModules } = useMappedState(({ global }: IStore) => ({
    role: global.role,
    installedModules: global.settings ? global.settings.installedModules || [] : []
  }));

  const [ { currentRole } ] = useCurrentUser();
  const [ { drop } ] = useDrop();
  const [ { currentOrder, isCreatingOrder }, { createOrder } ] = useOrder();

  const { isLoading: isLoadingProduct, product } = useProduct(
    productId,
    drop ? {
      branchId: drop.id,
      effectiveDate: installedModules.includes("price-change") && currentOrder?.mode === "edit" ?
        currentOrder?.data?.orderDeliveryDate || moment.utc().toDate() : undefined
    } :
    undefined
  );

  const [ { currentList } ] = useList();
  const checkPermission = useCheckUserPermission();

  /** Add a GTM Event on Load */
  React.useEffect(() => {
    if (productId && product && product.partNumber && product.id === productId) {
      gtmEvent("view_item", {
        ecommerce: {
          currency: "GBP",
          value: product?.prices ? product.prices[ 0 ]?.price || 0 : 0,
          items: [ productToGTMItem(product) ]
        }
      });
    }
  }, [
    gtmEvent,
    productId,
    product
  ]);

  const orderingNotAvailable = useOrderingNotAvailableForDay(drop);

  const editProductButton = React.useCallback(() => {
    if (
      role &&
      role.type === "admin" && checkPermission("amend-product") &&
      product &&
      (!currentOrder || currentOrder.mode !== "edit") &&
      (!currentList || currentList.mode !== "edit")
    ) {
      return (
        <ButtonLink to={`/admin/products/${product.id}/details`}>
          {`Edit ${terminology.product.singular.text}`}
        </ButtonLink>
      );
    }

    return null;
  }, [
    role,
    checkPermission,
    product,
    currentOrder,
    currentList,
    terminology.product.singular.text
  ]);

  // Check if the produt can be ordered.
  const productCanBeOrdered = React.useMemo(() => {
    if (product) {
      const validationDate = currentOrder.data ? currentOrder.data.orderDeliveryDate : null;

      return !orderingNotAvailable(validationDate, getProductOrderVariant(product), product);
    }

    return false;
  }, [
    product,
    currentOrder.data,
    orderingNotAvailable
  ]);

  return (
    <>
      <MainContentWrapper fill>
        {!product || product.id !== productId || isLoadingProduct ? <LoadingSpinner type="flat" /> : (
          <>
            <TopContentWrapper>
              {currentOrder.mode === "edit" && (
                <ProductWarning
                  product={product}
                  currentOrder={currentOrder}
                  terminology={terminology}
                  drop={drop}
                />
              )}

              <Grid.Bounds className="top-content-inner">
                <Grid.Box className={product.image ? "image-wrapper split-left hasImage" : "placeholder image-wrapper split-left"}>
                  <BrandedImage
                    s3Key={product.image}
                    alt={product.description}
                    level="public"
                  />
                </Grid.Box>

                <Grid.Box className="split-right">
                  <Text
                    weight="bold"
                    as="h2"
                    fontSize="h2"
                  >
                    {product.description}
                  </Text>

                  <PartNumber product={product} />

                  {product.leadTimeGroup && (
                    <Text className="display-info">
                      {`Lead time group: ${product.leadTimeGroup.customerDescription || product.leadTimeGroup.name}`}
                    </Text>
                  )}

                  {product.shelfLife && (
                    <Text className="display-info">
                      {`Shelf Life: ${product.shelfLife} ${product.shelfLifePeriod || "days"}`}
                    </Text>
                  )}

                  {product.orderVariant && <OrderVariantLabel orderVariant={product.orderVariant} />}

                  <ProductComponentLayoutDetails
                    customOnly
                    product={product}
                  />

                  <div className="product-ordering-controls-wrapper">
                    <ProductOrderingControls
                      product={product}
                      context={(currentOrder.mode === "edit" || (currentList.mode === "edit" && currentList.data && !canListOrderProduct(product, currentList.data.schedule))) ? "edit" : "view"}
                      CreateOrderButton={product => (role &&
                        role.type !== "admin" &&
                        (!currentOrder.data || (currentOrder.mode === "view" && drop && productCanBeOrdered)) &&
                        !currentList.data && (
                          <Grid.Box
                            style={{
                              position: "relative",
                              marginTop: "20px"
                            }}
                            fill={false}
                          >
                            <CreateOrderContext
                              direction="left"
                              drop={drop}
                              product={product}
                              loading={isCreatingOrder}
                              createDraftOrder={(date, options) => createOrder({
                                date,
                                origin: OrderOrigin.Manual,
                                products: [ product ],
                                orderVariantId: options?.orderVariant?.id,
                                dropOverride: options?.drop
                              })}
                            />
                          </Grid.Box>
                      ))}
                    />
                  </div>

                  {installedModules.includes("product-tag") && product.tags && (
                    <div className="product-tags-wrapper">
                      <ProductTagList
                        showAll
                        productTags={product.tags}
                      />
                    </div>
                  )}
                </Grid.Box>
              </Grid.Bounds>

              {installedModules.includes("promotion") && product.promotionalTags &&
          product.promotionalTags.length > 0 && (
            <div className="promotional-tags">
              {product.promotionalTags.map(promotionalTag => (
                <div
                  key={promotionalTag.id}
                  className="promotional-tag"
                >
                  <PromotionalTagLabel
                    color={promotionalTag.color}
                    text={promotionalTag.label}
                    position={AvailablePosition.Inherit}
                  />
                </div>
              ))}
            </div>
              )}
            </TopContentWrapper>

            <BottomContentWrapper>
              {product.barcode && (
                <Grid.Box className="barcode-container">
                  <Barcode value={product.barcode} />
                </Grid.Box>
              )}

              {!!(product.infoKey && product.infoKey.length) && (
                <Grid.Box className="about">
                  <Text as="h3">
                    About this product
                  </Text>

                  <HtmlBody body={product.infoKey} />
                </Grid.Box>
              )}

              {!!(product.ingredients && product.ingredients.length) && (
                <Grid.Box className="ingredients">
                  <Text as="h3">
                    Ingredients
                  </Text>

                  <HtmlBody body={product.ingredients} />
                </Grid.Box>
              )}

              {((product.nutritionValues && product.nutritionValues.length > 0) || product.nutritionImage) && (
                <Grid.Box className="nutrition">
                  <Text as="h3">
                    Nutritional Information
                  </Text>

                  {product.nutritionValues ?
                    <ProductNutritionValuesTable nutritionValues={product.nutritionValues} /> : (
                      <Grid.Box className={product.nutritionImage ? "image-wrapper hasImage" : "placeholder image-wrapper"}>
                        <S3Image
                          s3Key={product.nutritionImage}
                          alt={`${product.description} - Nutrition info`}
                          level="public"
                        />
                      </Grid.Box>
                    )}
                </Grid.Box>
              )}

              {product.productStatuses && product.currentStatus && role?.type === "admin" && (
                <div className="future-product-statuses">
                  <ProductStatuses
                    type="future"
                    currentStatus={product.currentStatus}
                    productStatuses={product.productStatuses}
                    displayOnly
                  />
                </div>
              )}
            </BottomContentWrapper>
          </>
        )}
      </MainContentWrapper>

      {showSideBar && (
        <InfoBarWrapper>
          <ProductInfoSidebar
            terminology={terminology}
            isLoading={isLoadingProduct}
            isAdmin={currentRole?.type === "admin"}
            product={product}
            primaryAction={editProductButton()}
          />
        </InfoBarWrapper>
      )}
    </>
  );
};

const BottomContentWrapper = styled(Grid.Box)`
  ${({ theme }) => css`

    h3  {
      margin-bottom: ${theme.spacing.padding.sm};
    }

    .about {
      margin-top: ${theme.spacing.padding.lg};
    }
    
    .barcode-container {
      margin-top: ${theme.spacing.margins.sm};
      max-width: 50%;
    }

    .ingredients {
      margin-top: ${theme.spacing.padding.lg}; 
    }

    .nutrition {
      margin-top: ${theme.spacing.padding.lg};
      
      .image-wrapper {
        max-width: 400px;
      }
    }
    
    .future-product-statuses {
      margin: ${theme.spacing.padding.lg} 0;
    }
  `}
`;

const TopContentWrapper = styled(Grid.Box)`
  ${({ theme }) => css`
    .top-content-inner {
      flex-direction: column;
    }
    .split-left {
      width: 100%;
    }

    .split-right {
      width: 100%;
    }

    .product-tags-wrapper {
      margin-top: ${theme.spacing.margins.sm};
    }

    .part-number-wrapper {
      margin-bottom: ${theme.spacing.margins.sm};
    }

    .display-info {
      display: block;
      color: ${theme.fonts.colors.secondary};
      margin-bottom: ${theme.spacing.margins.sm};
    }

    .product-detail-configurable-item {
      margin-bottom: ${theme.spacing.margins.sm};

      span:not(.variant) {
        color: ${theme.fonts.colors.secondary};
      }
    }

    .image-wrapper {
      position: relative;
      background-color: ${theme.colors.lightGrey};
      padding-bottom: 50%;
      margin-bottom: ${theme.spacing.margins.md};

      &.hasImage {
        background-color: transparent;
      }
      img {
        min-height: 100%;
        min-width: 100%;
        object-fit: contain;
        transform: translate(-50%, -50%);
        top: 50%;
        left: 50%;
        max-width: 100%;
        max-height: 100%;
        position: absolute;
      }
      &.placeholder img {
        width: 30px;
      }
    }

    .product-ordering-controls-wrapper {
      padding-top: ${theme.spacing.padding.sm};

      .pack-size-control-wrapper {
        margin-top: ${theme.spacing.margins.md};
        padding-top: 0;
        align-items: flex-start;

        .pack-size-selector-wrapper {
          div {
            width: max-content;
          }
        }

        .pack-quantity-label {
          justify-content: flex-start;
        }
      }
    }

    .quantity-buttons {
      margin-top: ${theme.spacing.margins.md};
      flex-direction: row-reverse;

      div {
        margin-right: 0px;
      }

      .layout-standard {
        margin-right: ${theme.spacing.margins.sm};
      }
    }

    .promotional-tags {
      display: flex;
    }

    .promotional-tag {
      margin-top: ${theme.spacing.margins.sm};
      margin-bottom: ${theme.spacing.margins.sm};
      margin-right: ${theme.spacing.margins.sm};
    }

    ${breakpoint("sm", theme)} {
      .top-content-inner {
        flex-direction: row;
      }
      .image-wrapper {
        padding-bottom: 30%;
      }
      .split-left {
        width: 50%;
        padding-right: ${theme.spacing.padding.md};
      }

      .split-right {
        width: 50%;
        padding-left: ${theme.spacing.padding.md};
      }
    }
  `}
`;

interface IProductWarningProps {
  product: GetProductResponse;
  currentOrder: ICurrentOrder;
  terminology: ITerminology;
  drop: IDrop | null;
}

const ProductWarning: React.ReactFCWithChildren<IProductWarningProps> = ({
  product, currentOrder, terminology, drop
}) => {
  const { orderVariants } = useMappedState((store: IStore) => ({ orderVariants: store.global.settings?.orderVariants || [] }));
  const orderingNotAvailableForDay = useOrderingNotAvailableForDay(drop);
  const orderVariant = product.orderVariant ?? _.find(orderVariants, ov => ov.id === product.orderVariantId);
  const allowedSubs = useProductIsAllowedSubs(product);

  if (!orderVariant) return null;
  const orderingNotAvailable = currentOrder.data?.orderDeliveryDate ? orderingNotAvailableForDay(currentOrder.data.orderDeliveryDate, orderVariant, product) : false;

  if (product.substitution) {
    return (
      <ProductWarningWrapper>
        <Text
          as="p"
          weight="bold"
        >
          {allowedSubs ?
            `${terminology.product.singular.text} is not available and will be automatically replaced by ${product.substitution.replacementProduct?.description} (${product.substitution.replacementProduct?.partNumber}).` :
            `${terminology.product.singular.text} is not available: ${product.substitution.message}`}
        </Text>
      </ProductWarningWrapper>
    );
  }

  if (orderingNotAvailable) {
    return (
      <ProductWarningWrapper>
        <Text
          as="p"
          weight="bold"
        >
          {orderingNotAvailable}
        </Text>
      </ProductWarningWrapper>
    );
  }

  return null;
};

const ProductWarningWrapper = styled(Grid.Box)`
  ${({ theme }) => css`
    width: 100%;
    padding: ${theme.spacing.padding.md};
    background: ${theme.colors.errorLight};
    margin-bottom: ${theme.spacing.margins.md};

    p {
      margin-bottom: 0;
    }
  `}
`;

export default ViewProductDetailsPage;
